import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Mail, ShieldCheck, Smartphone } from "lucide-react";

import PhoneNumberField from "@/components/linking/PhoneNumberField";
import Logo from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getRuntimeConfig } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import {
  buildLinkingPath,
  getLinkingPhoneState,
  inferDefaultPhoneCountry,
  rememberLinkingPhonePrefill,
} from "@/lib/linking-phone";
import { resolvePhoneAuthAccount, startPhoneAuth } from "@/lib/phone-auth";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser-client";

function normalizeOtpCode(value = "") {
  return String(value || "").replace(/\D/g, "").slice(0, 6);
}

export default function PhoneFirstAuth({
  errorMessage = "",
  onAuthenticated,
  onNeedsLink,
  onSkip,
  onUseEmail,
}) {
  const { tx, t } = useI18n();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [defaultCountry, setDefaultCountry] = useState(() => inferDefaultPhoneCountry());
  const [status, setStatus] = useState("idle");
  const [formError, setFormError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpPayload, setOtpPayload] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const phoneState = useMemo(() => getLinkingPhoneState(phoneNumber), [phoneNumber]);
  const canSubmitPhone = phoneState.canSubmit && !submitting && !verifying;
  const isOtpStep = status === "otp-sent";

  useEffect(() => {
    let cancelled = false;
    getRuntimeConfig()
      .then((config) => {
        if (!cancelled) {
          setDefaultCountry((currentCountry) => (
            inferDefaultPhoneCountry(undefined, config?.countryCode) || currentCountry
          ));
        }
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmitPhone = async (event) => {
    event.preventDefault();
    if (!phoneState.canSubmit || submitting || verifying) {
      setFormError("Enter your WhatsApp number.");
      return;
    }

    setSubmitting(true);
    setFormError("");
    setOtpError("");
    try {
      const payload = await startPhoneAuth(phoneState.normalizedPhoneNumber);
      if (payload?.status === "needs_link") {
        const phoneForLink = String(payload.phoneNumberE164 || phoneState.normalizedPhoneNumber || "").trim();
        rememberLinkingPhonePrefill(phoneForLink);
        onNeedsLink?.(payload.linkPath || buildLinkingPath({ phoneNumber: phoneForLink }));
        return;
      }
      if (payload?.status !== "otp_sent" || !payload.phoneNumberE164) {
        throw new Error("We could not start phone sign-in. Use email instead.");
      }
      setOtpPayload(payload);
      setOtpCode("");
      setStatus("otp-sent");
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "We could not start phone sign-in. Use email instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    const normalizedCode = normalizeOtpCode(otpCode);
    if (normalizedCode.length !== 6) {
      setOtpError("Enter the 6-digit code from WhatsApp.");
      return;
    }
    const client = getSupabaseBrowserClient();
    if (!client) {
      setOtpError("Supabase is not configured.");
      return;
    }

    setVerifying(true);
    setOtpError("");
    try {
      const { error } = await client.auth.verifyOtp({
        phone: otpPayload.phoneNumberE164,
        token: normalizedCode,
        type: "sms",
      });
      if (error) {
        throw error;
      }
      const accountPayload = await resolvePhoneAuthAccount(otpPayload.phoneNumberE164);
      onAuthenticated?.({
        userMode: "existing",
        accountId: accountPayload?.accountId,
        setupMode: accountPayload?.setupMode,
      });
    } catch (error) {
      setOtpError(error?.message || "That code did not work. Request a new one.");
    } finally {
      setVerifying(false);
    }
  };

  const handleUseAnotherNumber = () => {
    setStatus("idle");
    setOtpPayload(null);
    setOtpCode("");
    setOtpError("");
    setFormError("");
  };

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">
      <nav className="flex items-center justify-between px-5 py-4">
        <Logo size="sm" />
        <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={onSkip} disabled={submitting || verifying}>
          {t("common.back")}
        </Button>
      </nav>

      <div className="flex-1 flex items-center justify-center px-5 pb-24">
        <motion.div
          className="w-full max-w-md rounded-[2rem] border border-border bg-card/90 p-6 shadow-[0_20px_80px_-40px_rgba(15,23,42,0.5)]"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.22 }}
          data-testid="phone-first-auth-panel"
        >
          {isOtpStep ? (
            <div className="space-y-5 text-center" data-testid="phone-auth-otp-panel">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                <ShieldCheck className="h-7 w-7" />
              </div>
              <div className="space-y-2">
                <h1 className="font-display text-2xl font-bold">{tx("Enter your WhatsApp code")}</h1>
                <p className="text-sm text-muted-foreground">
                  {tx("We sent a 6-digit Min sign-in code on WhatsApp to")}{" "}
                  <span className="font-medium text-foreground">{otpPayload?.maskedPhoneNumber || tx("your number")}</span>.
                </p>
              </div>
              {otpError ? <p className="text-sm text-destructive">{tx(otpError)}</p> : null}
              <form className="space-y-3 rounded-2xl border border-border/70 bg-muted/40 p-4 text-left" onSubmit={handleVerifyOtp}>
                <label htmlFor="phone-auth-otp-code" className="block text-sm font-medium text-foreground">
                  {tx("6-digit code")}
                </label>
                <Input
                  id="phone-auth-otp-code"
                  value={otpCode}
                  disabled={verifying}
                  onChange={(event) => setOtpCode(normalizeOtpCode(event.target.value))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="123456"
                  className="h-12 rounded-2xl text-center text-lg tracking-[0.24em]"
                />
                <Button
                  type="submit"
                  className="h-12 w-full rounded-2xl text-base font-semibold"
                  disabled={verifying || otpCode.length !== 6}
                >
                  {verifying ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {tx("Verifying…")}
                    </>
                  ) : (
                    tx("Continue with code")
                  )}
                </Button>
              </form>
              <div className="grid w-full grid-cols-2 gap-3">
                <Button variant="outline" className="rounded-2xl" onClick={handleUseAnotherNumber} disabled={verifying}>
                  {tx("Use another number")}
                </Button>
                <Button variant="ghost" className="text-muted-foreground" onClick={onUseEmail} disabled={verifying}>
                  {tx("Use email instead")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Smartphone className="h-7 w-7" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    {tx("Sign in or sign up")}
                  </p>
                  <h1 className="font-display text-2xl font-bold">{tx("Use your WhatsApp number")}</h1>
                </div>
              </div>
              {errorMessage ? <p className="text-sm text-destructive">{tx(errorMessage)}</p> : null}
              <form className="space-y-4" onSubmit={handleSubmitPhone}>
                <div className="space-y-2">
                  <label htmlFor="phone-auth-number" className="block text-sm font-medium text-foreground">
                    {tx("Your WhatsApp number")}
                  </label>
                  <PhoneNumberField
                    id="phone-auth-number"
                    defaultCountry={defaultCountry}
                    value={phoneNumber}
                    onChange={setPhoneNumber}
                    disabled={submitting}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {tx("If this number is already linked, we will send a Min code on WhatsApp. If not, we will help you link it.")}
                </p>
                {formError ? <p className="text-sm text-destructive">{tx(formError)}</p> : null}
                <Button
                  type="submit"
                  size="lg"
                  disabled={!canSubmitPhone}
                  className="h-14 w-full rounded-2xl text-base font-semibold"
                  data-testid="phone-auth-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {tx("Checking number…")}
                    </>
                  ) : (
                    <>
                      {tx("Continue")} <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
              <Button
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={onUseEmail}
                disabled={submitting}
                data-testid="phone-auth-email-fallback"
              >
                <Mail className="h-4 w-4" />
                {tx("Use email instead")}
              </Button>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
